import React, { memo, useCallback } from 'react'
import { makeStyles } from '@material-ui/styles'
import { useTranslation, Translation } from 'react-i18next'
import Divider from '@material-ui/core/Divider'
import {
	Grid,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	useTheme,
	Typography,
} from '@material-ui/core'
import * as _ from 'lodash'
import Button from '@material-ui/core/Button'
import Fab from '@material-ui/core/Fab'
import Input from '@material-ui/core/Input'
import Datetime from 'react-datetime'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import useMediaQuery from '@material-ui/core/useMediaQuery/useMediaQuery'

import { EUserOnboardingActionTypes } from 'models'
import { StoreValue } from 'store'
import styles from '../styles'
import { UserOnboardingStateValue } from '../reducer'
import classNames from 'classnames'
import { useRoLocationsCollectionQuery } from 'graphql/__generated__/graphql'

const useStyles = makeStyles(styles)

const Step3View: React.FunctionComponent<any> = props => {
	const { experience, error, handleNext, handleBack, userOnBoardDispatch } =
		props
	const classes = useStyles()

	const {
		title,
		description,
		startDatetime,
		endDatetime,
		region,
		location,
		company,
		position,
		isCurrent,
		isPresent,
	} = experience
	const theme = useTheme()
	const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))
	const { t } = useTranslation()
	const { data: regions } = useRoLocationsCollectionQuery({
		variables: {
			data: {
				type: {
					in: ['Judeţe', 'National capital'],
				},
			},
		},
	})
	const { data: locations } = useRoLocationsCollectionQuery({
		skip: !region,
		variables: {
			data: {
				region2: { eq: region && region },
				not: { type: { in: ['Judeţe', 'National capital'] } },
			},
		},
	})
	const isNotValidStep = useCallback(() => {
		return (
			_.isEmpty(title) ||
			_.isEmpty(description) ||
			_.isEmpty(startDatetime) ||
			(isPresent ? false : _.isEmpty(endDatetime)) ||
			_.isEmpty(region) ||
			_.isEmpty(location) ||
			_.isEmpty(company)
		)
	}, [
		company,
		description,
		endDatetime,
		location,
		region,
		startDatetime,
		title,
		isPresent,
	])

	const goToNextStep = useCallback(
		_error => {
			if (isNotValidStep()) {
				userOnBoardDispatch({
					type: EUserOnboardingActionTypes.setError,
					errors: {
						experience: {
							title: _.isEmpty(title),
							description: _.isEmpty(description),
							startDatetime: _.isEmpty(startDatetime),
							endDatetime: isPresent
								? false
								: _.isEmpty(endDatetime),
							region: _.isEmpty(region),
							location: _.isEmpty(location),
							company: _.isEmpty(company),
						},
					},
				})
				return
			}
			handleNext()
		},
		[
			isPresent,
			handleNext,
			isNotValidStep,
			userOnBoardDispatch,
			company,
			description,
			endDatetime,
			location,
			region,
			startDatetime,
			title,
		],
	)
	const handleTitleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			userOnBoardDispatch({
				type: EUserOnboardingActionTypes.setExperienceTitle,
				title: event.target.value,
			})
		},
		[userOnBoardDispatch],
	)
	const handleDescriptionChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			userOnBoardDispatch({
				type: EUserOnboardingActionTypes.setExperienceDesc,
				description: event.target.value,
			})
		},
		[userOnBoardDispatch],
	)
	const handleLocationChange = (event: any) => {
		userOnBoardDispatch({
			type: EUserOnboardingActionTypes.setExperienceLocation,
			location: event.target.value,
		})
	}
	const handleRegionChange = useCallback(
		(event: any) => {
			userOnBoardDispatch({
				type: EUserOnboardingActionTypes.setExperienceRegion,
				region: event.target.value,
			})
		},
		[userOnBoardDispatch],
	)
	const handlePositionChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			userOnBoardDispatch({
				type: EUserOnboardingActionTypes.setExperiencePosition,
				position: event.target.value,
			})
		},
		[userOnBoardDispatch],
	)
	const handleCompanyChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			userOnBoardDispatch({
				type: EUserOnboardingActionTypes.setExperienceCompany,
				company: event.target.value,
			})
		},
		[userOnBoardDispatch],
	)
	const handleIsPresentChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			userOnBoardDispatch({
				type: EUserOnboardingActionTypes.setExperienceIsPresent,
				isPresent: event.target.checked,
			})
		},
		[userOnBoardDispatch],
	)
	const handleIsCurrentChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			userOnBoardDispatch({
				type: EUserOnboardingActionTypes.setExperienceIsCurrent,
				isCurrent: event.target.checked,
			})
		},
		[userOnBoardDispatch],
	)
	const updateEndDatetime = useCallback(
		dt => {
			userOnBoardDispatch({
				type: EUserOnboardingActionTypes.setExperienceEndDatetime,
				endDatetime:
					dt && dt.isAfter(startDatetime)
						? dt
						: startDatetime.add(5, 'minutes'),
			})
		},
		[userOnBoardDispatch, startDatetime],
	)
	const updateStartDatetime = useCallback(
		dt => {
			userOnBoardDispatch({
				type: EUserOnboardingActionTypes.setExperienceStartDatetime,
				startDatetime: dt,
			})
			if (endDatetime && !endDatetime.isAfter(dt))
				userOnBoardDispatch({
					type: EUserOnboardingActionTypes.setExperienceEndDatetime,
					endDatetime: dt.add(5, 'minutes'),
				})
		},
		[userOnBoardDispatch, endDatetime],
	)
	return (
		<div>
			<Grid container direction={'row'} spacing={2}>
				<Grid container spacing={0}>
					<Grid item xs={12}>
						<Typography variant="subtitle1" gutterBottom>
							<div style={{ padding: '8px' }}>
								{t(
									'Please enter the necessary information about the latest professional or educational experience.',
								)}
							</div>
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<FormControl className={classes.formControl}>
							<InputLabel htmlFor="experience_title">
								<Translation key="titleRequired">
									{t => (
										<div
											className={classNames(
												classes.labelField,
												{
													[classes.labelFieldError]:
														error.experience.title,
												},
											)}
										>
											{t('Title')}
											<span
												className={classNames(
													classes.requiredField,
													{
														[classes.labelFieldError]:
															error.experience
																.title,
													},
												)}
											>
												{`(${t('Required')})`}
											</span>
										</div>
									)}
								</Translation>
							</InputLabel>

							<Input
								id="experience_title"
								value={title}
								// error={error.experience.title}
								onChange={handleTitleChange}
								className={classes.textField}
							/>
						</FormControl>
					</Grid>
					<Grid item xs={12}>
						<FormControl className={classes.formControl}>
							<InputLabel htmlFor="experience_description">
								<Translation key="descriptionRequired">
									{t => (
										<div
											className={classNames(
												classes.labelField,
												{
													[classes.labelFieldError]:
														error.experience
															.description,
												},
											)}
										>
											{t('Description')}
											<span
												className={classNames(
													classes.requiredField,
													{
														[classes.labelFieldError]:
															error.experience
																.description,
													},
												)}
											>
												{`(${t('Required')})`}
											</span>
										</div>
									)}
								</Translation>
							</InputLabel>
							<Input
								id="experience_description"
								value={description}
								// error={error.experience.description}
								onChange={handleDescriptionChange}
								multiline={true}
								rows={2}
								rowsMax={4}
							/>
						</FormControl>
					</Grid>
					<Grid item xs={6} sm={3}>
						<Datetime
							timeFormat={false}
							// isValidDate={(current: any) =>
							// 	current.isAfter(Datetime.moment())
							// }
							value={startDatetime}
							closeOnSelect={true}
							onChange={updateStartDatetime}
							inputProps={{
								readOnly: true,
							}}
							renderInput={(
								props: any,
								open: any,
								close: any,
							) => {
								return (
									<FormControl
										style={{
											maxWidth: '70%',
											// margin: theme.spacing(1),
											marginRight: theme.spacing(2),
										}}
										className={classes.formControl}
									>
										<InputLabel htmlFor="experience_startDate">
											<Translation key="startDateRequired">
												{t => (
													<div
														className={classNames(
															classes.labelField,
															{
																[classes.labelFieldError]:
																	error
																		.experience
																		.startDatetime,
															},
														)}
													>
														{t('Start')}
														<span
															className={classNames(
																classes.requiredField,
																{
																	[classes.labelFieldError]:
																		error
																			.experience
																			.startDatetime,
																},
															)}
														>
															{`(${t(
																'Required',
															)})`}
														</span>
													</div>
												)}
											</Translation>
										</InputLabel>
										<Input
											// error={
											// 	error.experience.startDatetime
											// }
											{...props}
											onClick={open}
										/>
									</FormControl>
								)
							}}
						/>
					</Grid>
					<Grid item xs={6} sm={3}>
						<Datetime
							timeFormat={false}
							open={isPresent ? false : undefined}
							isValidDate={(current: any) =>
								current.isAfter(startDatetime)
							}
							value={!isPresent ? endDatetime : undefined}
							closeOnSelect={true}
							onChange={updateEndDatetime}
							inputProps={{
								readOnly: true,
							}}
							renderInput={(
								props: any,
								open: any,
								close: any,
							) => {
								return (
									<FormControl
										style={{
											maxWidth: '70%',
											//margin: theme.spacing(1),
											marginRight: theme.spacing(2),
										}}
										className={classes.formControl}
									>
										<InputLabel
											htmlFor="experience_endDate"
											disabled={isPresent}
										>
											<Translation key="endDateRequired">
												{t => (
													<div
														className={classNames(
															classes.labelField,
															{
																[classes.labelFieldError]:
																	error
																		.experience
																		.endDatetime,
															},
														)}
													>
														{t('End')}
														<span
															className={classNames(
																classes.requiredField,
																{
																	[classes.labelFieldError]:
																		error
																			.experience
																			.endDatetime,
																},
															)}
														>
															{`(${t(
																'Required',
															)})`}
														</span>
													</div>
												)}
											</Translation>
										</InputLabel>
										<Input
											disabled={isPresent}
											// error={error.experience.endDatetime}
											{...props}
											onClick={open}
										/>
									</FormControl>
								)
							}}
						/>
					</Grid>
					<Grid item xs={12} sm={4}>
						<FormControlLabel
							style={{
								paddingTop: theme.spacing(1),
								marginLeft: fullScreen ? 0 : theme.spacing(2),
							}}
							control={
								<Checkbox
									value={isPresent}
									checked={isPresent}
									onChange={handleIsPresentChange}
									color="primary"
								/>
							}
							label={t('Present')}
						/>
					</Grid>
					<Grid item xs={12} style={{ display: 'inline-flex' }}>
						<FormControl className={classes.formControl}>
							<InputLabel htmlFor="experience-region">
								<Translation key="regionRequired">
									{t => (
										<div
											className={classNames(
												classes.labelField,
												{
													[classes.labelFieldError]:
														error.experience.region,
												},
											)}
										>
											{t('Region')}
											<span
												className={classNames(
													classes.requiredField,
													{
														[classes.labelFieldError]:
															error.experience
																.region,
													},
												)}
											>
												{`(${t('Required')})`}
											</span>
										</div>
									)}
								</Translation>
							</InputLabel>
							<Select
								value={region ? region : ''}
								onChange={handleRegionChange}
								// error={error.experience.region}
								inputProps={{
									name: t('Region'),
									id: 'experience-region',
								}}
							>
								{regions &&
									regions.roLocationsCollection &&
									regions.roLocationsCollection.edges?.map(
										(item: any) => {
											return (
												<MenuItem
													key={item.node?.id}
													value={item.node?.name}
												>
													{item.node?.name}
												</MenuItem>
											)
										},
									)}
							</Select>
						</FormControl>
						<FormControl className={classes.formControl}>
							<InputLabel htmlFor="experience-location">
								<Translation key="locationRequired">
									{t => (
										<div
											className={classNames(
												classes.labelField,
												{
													[classes.labelFieldError]:
														error.experience
															.location,
												},
											)}
										>
											{t('Location')}
											<span
												className={classNames(
													classes.requiredField,
													{
														[classes.labelFieldError]:
															error.experience
																.location,
													},
												)}
											>
												{`(${t('Required')})`}
											</span>
										</div>
									)}
								</Translation>
							</InputLabel>
							<Select
								value={location ? location : ''}
								disabled={!region}
								onChange={handleLocationChange}
								// error={error.experience.location}
								inputProps={{
									name: t('Location'),
									id: 'experience-location',
								}}
							>
								{locations &&
									locations.roLocationsCollection &&
									locations.roLocationsCollection.edges?.map(
										(item: any) => {
											return (
												<MenuItem
													key={item.node.id}
													value={item.node.id}
												>
													{item.node.name}
												</MenuItem>
											)
										},
									)}
							</Select>
						</FormControl>
					</Grid>
					<Grid
						item
						sm={6}
						xs={12}
						style={{ display: 'inline-flex' }}
					>
						<FormControl className={classes.formControl}>
							<InputLabel htmlFor="experience_company">
								<Translation key="locationRequired">
									{t => (
										<div
											className={classNames(
												classes.labelField,
												{
													[classes.labelFieldError]:
														error.experience
															.company,
												},
											)}
										>
											{t('Company/Institution')}
											<span
												className={classNames(
													classes.requiredField,
													{
														[classes.labelFieldError]:
															error.experience
																.company,
													},
												)}
											>
												{`(${t('Required')})`}
											</span>
										</div>
									)}
								</Translation>
							</InputLabel>
							<Input
								id="experience_company"
								value={company}
								onChange={handleCompanyChange}
								// error={error.experience.company}
								multiline={true}
								rows={1}
								rowsMax={1}
							/>
						</FormControl>
					</Grid>
					<Grid
						item
						sm={6}
						xs={12}
						style={{ display: 'inline-flex' }}
					>
						<FormControl className={classes.formControl}>
							<InputLabel htmlFor="experience_position">
								{t('Position/Courses')}
							</InputLabel>
							<Input
								id="experience_position"
								value={position}
								onChange={handlePositionChange}
								multiline={true}
								rows={1}
								rowsMax={1}
							/>
						</FormControl>
					</Grid>
					<Grid item>
						<FormControlLabel
							style={{
								paddingTop: theme.spacing(1),
								marginLeft: theme.spacing(-0.5),
							}}
							control={
								<Checkbox
									value={isCurrent}
									checked={isCurrent}
									onChange={handleIsCurrentChange}
									color="primary"
								/>
							}
							label={t('Use this as my current position')}
						/>
					</Grid>
				</Grid>

				<Grid item xs={12}>
					<Divider className={classes.divider} />
				</Grid>
				<Grid item xs={12}>
					<div className={classes.btnsContainer}>
						<Button
							onClick={handleBack}
							// className={classes.button}
						>
							{t('Back')}
						</Button>
						{/* <Button onClick={handleSkip}>{t('Skip')}</Button> */}
						<Fab
							onClick={goToNextStep}
							className={classes.nextBtn}
							variant="extended"
							size="medium"
							color="primary"
							aria-label="Add"
						>
							{t('Next') || ''}
						</Fab>
					</div>
				</Grid>
			</Grid>
		</div>
	)
}
const Step3WithStoreValue = (props: any) => (
	<StoreValue keys={['user.id|storeUserId']}>
		<Step3View {...props} />
	</StoreValue>
)
const Step3 = memo((props: any) => (
	<UserOnboardingStateValue
		keys={['id', 'userOnBoardDispatch', 'experience', 'error']}
	>
		<Step3WithStoreValue {...props} />
	</UserOnboardingStateValue>
))

export default Step3
